import moment from 'moment';
import projectTaskCategoryTypes from '~/lookups/project-task-category-types';
import buildTree from '../components/buildTree';

const DAY_WIDTH = { day: 28, week: 16, month: 6 };

export const ROW_H = 40;
const HEADER_H = 48; // always two tiers (major on top, minor below)
const BAR_PAD = 8;
const MILESTONE_MIN_HALF = 4; // visual floor: keeps a recognizable diamond at the narrowest scales
const MILESTONE_HIT_HALF = 7; // hit-target floor: comfortable click/drop zone regardless of visual size

// Minor tier (bottom row): grid step, arithmetic unit, label per scale.
const STEP = { day: 'day', week: 'isoWeek', month: 'month' };
const ADD = { day: 'days', week: 'weeks', month: 'months' };
const LABEL = { day: 'D', week: 'MMM D', month: 'MMM' };

// Major tier (top row): month groups day/week, year groups month.
const MAJOR_BOUNDARY = { day: 'month', week: 'month', month: 'year' };
const MAJOR_ADD = { day: 'months', week: 'months', month: 'years' };
const MAJOR_LABEL = { day: 'MMM YYYY', week: 'MMM YYYY', month: 'YYYY' };

// Lead-up / runway pad around the data, in minor units.
const PAD = { day: 7, week: 3, month: 2 };

// Minimum visible days for a major segment to fit its label; nudges range edges.
const MIN_MAJOR_DAYS = { day: 3, week: 7, month: 0 };

// Elbow offset: how far the line runs straight off the predecessor before it turns toward the successor.
export const ARROW_GAP = 12;

// FS elbow path from the predecessor's connector node (x1) to the successor's start. Simple elbow
// when the successor is far enough ahead; otherwise routes around so both ends still point forward.
// trimStart only moves the start point along the (horizontal) first segment — never past the first
// turn — so the trajectory is identical with or without it.
function arrowPath({ x1, y1, x2, y2, trimStart = 0 }) {
  const GAP = ARROW_GAP;
  if (x2 - GAP >= x1) {
    return `M ${Math.min(x1 + trimStart, x2 - GAP)} ${y1} H ${x2 - GAP} V ${y2} H ${x2}`;
  }
  const channelY = y1 + (y2 >= y1 ? ROW_H / 2 : -ROW_H / 2);
  return `M ${Math.min(x1 + trimStart, x1 + GAP)} ${y1} H ${x1 + GAP} V ${channelY} H ${x2 - GAP} V ${y2} H ${x2}`;
}

// FS arrow between two bars. Milestones anchor to their diamond's side vertices, not the bar box edge.
export const rightAnchor = (bar) => (bar.isMilestone ? bar.x + bar.w / 2 + bar.mHalf : bar.x + bar.w);
export const leftAnchor = (bar) => (bar.isMilestone ? bar.x + bar.w / 2 - bar.mHalf : bar.x);

export function arrowBetween(from, to, trimStart = 0) {
  // Dependencies originate at the predecessor's connector node — a stub (ARROW_GAP) past its finish
  // edge. trimStart begins the stroke further along the first segment (e.g. past the node dot for the
  // selection highlight) without moving any turn, so the trajectory is unchanged.
  return arrowPath({
    x1: rightAnchor(from) + ARROW_GAP,
    y1: from.y + from.h / 2,
    x2: leftAnchor(to),
    y2: to.y + to.h / 2,
    trimStart,
  });
}

// DFS flatten; skips children of collapsed parents.
function flatten(nodes, depth, out, isExpanded) {
  for (const node of nodes) {
    const hasChildren = node.children.length > 0;
    const expanded = isExpanded(node.task.id);
    out.push({ task: node.task, depth, hasChildren, expanded });
    if (hasChildren && expanded) flatten(node.children, depth + 1, out, isExpanded);
  }
  return out;
}

export default function buildGanttModel({
  tasks,
  unit = 'week',
  minWidth = 0,
  isExpanded = () => true,
  projectStart = null,
  projectEnd = null,
}) {
  const dayWidth = DAY_WIDTH[unit] ?? DAY_WIDTH.week;
  const step = STEP[unit] ?? STEP.week;
  const add = ADD[unit] ?? ADD.week;
  const pad = PAD[unit] ?? PAD.week;
  const label = LABEL[unit] ?? LABEL.week;

  const nodes = [...tasks]
    .sort((a, b) => Number(a.position) - Number(b.position))
    .map((task) => ({ id: task.id, parentId: task.parentId, task }));
  const rows = flatten(buildTree(nodes), 0, [], isExpanded);

  // Bounds include project start/end so the range always covers the project window.
  const dated = tasks.filter((t) => t.start && t.end);
  const starts = dated.map((t) => moment(t.start));
  const ends = dated.map((t) => moment(t.end));
  if (projectStart) starts.push(moment(projectStart));
  if (projectEnd) ends.push(moment(projectEnd));
  const min = starts.length ? moment.min(starts) : moment();
  const max = ends.length ? moment.max(ends) : moment().add(1, 'month');
  const dataStart = min.clone().startOf(step);
  const rangeStart = dataStart.clone().subtract(pad, add);
  const rangeEnd = max.clone().endOf(step).add(pad, add);

  // Nudge edges so the first/last major segment has room for its label.
  const minMajor = MIN_MAJOR_DAYS[unit] ?? 0;
  if (minMajor) {
    const majorBoundary = MAJOR_BOUNDARY[unit] ?? MAJOR_BOUNDARY.week;
    while (rangeStart.clone().endOf(majorBoundary).diff(rangeStart, 'days') + 1 < minMajor) {
      rangeStart.subtract(1, add);
    }
    while (rangeEnd.diff(rangeEnd.clone().startOf(majorBoundary), 'days') + 1 < minMajor) {
      rangeEnd.add(1, add);
    }
  }

  const xFor = (date) => moment(date).diff(rangeStart, 'days') * dayWidth;
  // Bar width is end-inclusive: a single-day task (start == end) still spans one column.
  const barWidth = (start, end) => (moment(end).diff(moment(start), 'days') + 1) * dayWidth;
  const width = Math.max((rangeEnd.diff(rangeStart, 'days') + 1) * dayWidth, minWidth);
  const height = rows.length * ROW_H;
  const startX = dated.length ? xFor(dataStart) : 0;
  const projectStartX = projectStart ? xFor(projectStart) : null;
  // End-inclusive like the bars: the line sits at the end of the last day, not its start.
  const projectEndX = projectEnd ? xFor(projectEnd) + dayWidth : null;

  const barById = new Map();
  const bars = [];
  // Milestone diamond: visual half-extent sized to the day-column it occupies (capped at the bar
  // half-height, floored so it stays a recognizable diamond). The hit/drop target is decoupled with
  // its own larger floor, so the marker can render small at month scale while staying easy to grab.
  const milestoneHalf = Math.max(MILESTONE_MIN_HALF, Math.min(dayWidth / 2, (ROW_H - BAR_PAD * 2) / 2));
  const milestoneHit = Math.max(milestoneHalf, MILESTONE_HIT_HALF);
  rows.forEach(({ task }, i) => {
    if (!task.start || !task.end) return;
    const bar = {
      taskId: task.id,
      x: xFor(task.start),
      w: barWidth(task.start, task.end),
      y: i * ROW_H + BAR_PAD,
      h: ROW_H - BAR_PAD * 2,
      mHalf: milestoneHalf,
      mHit: milestoneHit,
      isRollup: task.type === projectTaskCategoryTypes.rollup.id,
      isMilestone: task.type === projectTaskCategoryTypes.milestone.id,
      isCompleted: task.statusId === 'completed',
      statusId: task.statusId,
    };
    bars.push(bar);
    barById.set(task.id, bar);
  });

  // Finish-to-Start arrows.
  const arrows = [];
  tasks.forEach((task) => {
    const from = barById.get(task.id);
    if (!from) return;
    (task.successors || []).forEach((s) => {
      const to = barById.get(s.id);
      if (!to) return;
      arrows.push({
        id: `${task.id}->${s.id}`,
        predecessorId: task.id,
        successorId: s.id,
        d: arrowBetween(from, to),
      });
    });
  });

  // Two-tier ticks: minor per scale unit, major at true month/year boundaries.
  const minorTicks = [];
  for (const cursor = rangeStart.clone(); xFor(cursor) <= width; cursor.add(1, add)) {
    minorTicks.push({ x: xFor(cursor), label: cursor.format(label) });
  }

  const majorBoundary = MAJOR_BOUNDARY[unit] ?? MAJOR_BOUNDARY.week;
  const majorAdd = MAJOR_ADD[unit] ?? MAJOR_ADD.week;
  const majorFormat = MAJOR_LABEL[unit] ?? MAJOR_LABEL.week;
  const majorTicks = [];
  for (const cursor = rangeStart.clone().startOf(majorBoundary); xFor(cursor) <= width; cursor.add(1, majorAdd)) {
    majorTicks.push({ x: xFor(cursor), label: cursor.format(majorFormat) });
  }

  const today = moment();
  const todayX = xFor(today) >= 0 && xFor(today) <= width ? xFor(today) : null;

  return {
    rows,
    bars,
    arrows,
    minorTicks,
    majorTicks,
    headerHeight: HEADER_H,
    dayWidth,
    xFor,
    barWidth,
    width,
    height,
    todayX,
    startX,
    projectStartX,
    projectEndX,
  };
}
